import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { DiscardDialog } from "@/components/DiscardDialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import { createEventCategory, updateEventCategory, uploadStoreImage } from "@/lib/api/admin";
import type { EventCategory, EventCategoryVisibilityScope } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/TextInput";
import { Label } from "@/components/ui/label";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { ImageUpload } from "@/components/ImageUpload";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/Spinner";

const URL_PARAM_PATTERN = /^[a-z0-9-]+$/;

const VISIBILITY_SCOPE_OPTIONS: { value: EventCategoryVisibilityScope; label: string }[] = [
  { value: "all", label: "Both" },
  { value: "public", label: "Public only" },
  { value: "unlisted", label: "Unlisted only" },
];

const schema = z.object({
  name: z.string().min(1, "Name is required.").max(100, "Name must be 100 characters or fewer."),
  urlParam: z
    .string()
    .min(1, "URL param is required.")
    .max(50, "URL param must be 50 characters or fewer.")
    .refine((v) => URL_PARAM_PATTERN.test(v), "Lowercase letters, numbers, and hyphens only."),
  visibilityScope: z.enum(["public", "unlisted", "all"]),
});

type FormValues = z.infer<typeof schema>;

type EventCategoryFormDialogProps = {
  open: boolean;
  onClose: () => void;
  storeId: string;
  category?: EventCategory;
};

export function EventCategoryFormDialog({
  open,
  onClose,
  storeId,
  category,
}: EventCategoryFormDialogProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const isEditing = !!category;
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [bannerUrl, setBannerUrl] = useState<string | undefined>(category?.banner_url);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: category?.name ?? "",
      urlParam: category?.url_param ?? "",
      visibilityScope: category?.visibility_scope ?? "all",
    },
  });

  const urlParamField = register("urlParam");

  useEffect(() => {
    if (open) {
      reset({
        name: category?.name ?? "",
        urlParam: category?.url_param ?? "",
        visibilityScope: category?.visibility_scope ?? "all",
      });
      setBannerFile(null);
      setBannerUrl(category?.banner_url);
    }
  }, [open, category, reset]);

  const bannerDirty = bannerFile !== null || bannerUrl !== category?.banner_url;

  function tryClose() {
    if (isDirty || bannerDirty) {
      setDiscardOpen(true);
    } else {
      onClose();
    }
  }

  async function onSubmit(values: FormValues) {
    setIsSubmitting(true);
    try {
      let finalBannerUrl = bannerUrl;
      if (bannerFile) {
        const res = await uploadStoreImage(user!, storeId, bannerFile, "event_category_banner");
        finalBannerUrl = res.url;
      }

      const payload = {
        name: values.name.trim(),
        url_param: values.urlParam.trim(),
        banner_url: finalBannerUrl,
        visibility_scope: values.visibilityScope,
      };

      if (isEditing) {
        await updateEventCategory(user!, storeId, category.id, payload);
      } else {
        await createEventCategory(user!, storeId, payload);
      }

      await qc.invalidateQueries({ queryKey: ["event-categories", storeId] });
      toast.success(isEditing ? "Category updated." : "Category created.");
      onClose();
    } catch (e) {
      setError("root", { message: (e as Error).message });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{isEditing ? "Edit Category" : "New Category"}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5 py-2">
            <TextInput
              id="uec-name"
              label="Name *"
              placeholder="e.g. EDM Night"
              maxLength={100}
              error={errors.name?.message}
              {...register("name")}
            />

            <TextInput
              id="uec-url-param"
              label="URL param *"
              placeholder="e.g. edm"
              maxLength={50}
              error={errors.urlParam?.message}
              {...urlParamField}
              onChange={(e) => {
                e.target.value = e.target.value.toLowerCase();
                urlParamField.onChange(e);
              }}
            />

            <div className="space-y-1.5">
              <Label>Visibility</Label>
              <Controller
                control={control}
                name="visibilityScope"
                render={({ field }) => (
                  <SegmentedToggle
                    value={field.value}
                    onChange={field.onChange}
                    options={VISIBILITY_SCOPE_OPTIONS}
                    disabled={isSubmitting}
                  />
                )}
              />
            </div>

            <ImageUpload
              id="uec-banner"
              label="Banner (optional, 3:1)"
              aspectRatio="3:1"
              enableCrop
              currentUrl={bannerUrl}
              onFileSelect={(file) => setBannerFile(file)}
              onClear={() => {
                setBannerFile(null);
                setBannerUrl(undefined);
              }}
            />

            {errors.root?.message && (
              <p className="text-sm text-destructive">{errors.root.message}</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={tryClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner className="size-4 border-background" />
                ) : isEditing ? (
                  "Save"
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}
